<template>
  <div class="page" :class="pageSizeClass">
    <!-- 상단 커버 이미지 -->
    <div class="cover-image-section">
      <img
        class="cover-image"
        src="@/assets/cover/281.png"
        alt="Cover Image"
      />
    </div>

    <!-- 학회 정보 -->
    <div class="society-info-section">
      <img
        class="society-logo"
        src="@/assets/cover/iskal_logo.png"
        alt="ISKAL Logo"
      />
      <div class="society-text">
        <p class="society-name-en">
          The International Society of Korean Applied Linguistics
        </p>
        <p class="society-name-ko">국제한국어응용언어학회</p>
      </div>
    </div>

    <!-- 구분선 -->
    <hr class="divider" />

    <!-- 논문 목록 -->
    <div class="paper-list-section">
      <div
        v-for="(paper, index) in paperList"
        :key="index"
        class="paper-item"
      >
        <p class="paper-title-eng">{{ paper.title_eng }}</p>
        <p class="paper-author">{{ paper.authors }}</p>
      </div>
    </div>

    <!-- ISSN 정보 (하단) -->
    <div class="issn-section">
      <p class="issn-text">ISSN: 2799-9106</p>
    </div>
  </div>
</template>

<script>
export default {
  name: "article-committee-page",
  props: {
    group: {
      type: Object,
      required: false,
      default: () => {},
    },
    journal: {
      type: Object,
      required: false,
      default: () => {},
    },
    items: {
      type: Array,
      required: false,
      default: () => [],
    },
    pageSize: {
      type: String,
      required: false,
      default: "A5",
    },
  },
  computed: {
    pageSizeClass() {
      return this.pageSize?.toLowerCase() || "a5";
    },
    paperList() {
      if (this.items && this.items.length > 0) {
        // 빈페이지(category === 'empty')는 논문 목록에서 제외
        return this.items
          .filter(item => item.category !== 'empty')
          .map(item => ({
            title_eng: this.formatTitleEng(item.title_eng, item.subtitle_eng),
            authors: this.formatAuthorsEn(item.authors),
          }));
      }
      return [];
    },
  },
  methods: {
    formatTitleEng(titleEng, subtitleEng) {
      const title = titleEng || '';
      const subtitle = subtitleEng || '';
      if (!subtitle) return title;
      if (!title) return subtitle;
      return `${title}: ${subtitle}`;
    },
    formatAuthorsEn(authors) {
      if (!authors || !Array.isArray(authors)) return '';
      //return authors.map(a => a.name_eng || '').filter(Boolean).join(' · ');
      return authors.map(a => a.name_eng || '').filter(Boolean).join(' / ');
    },
  },
};
</script>

<style lang="scss" scoped>
$primary-font-color: #231815;
$secondary-font-color: #666666;
$background-color: #ffffff;

// A4 패딩
$a4-vertical-padding: 50px;
$a4-horizontal-padding: 60px;

// A5 패딩
$a5-vertical-padding: 35px;
$a5-horizontal-padding: 40px;

$sans-font-family: "Sandoll GtNeoCond", "Noto Sans KR", sans-serif;

$a4-width: 794px;
$a4-height: 1122px;
$a5-width: 559px;
$a5-height: 794px;

.page {
  background-color: $background-color;
  position: relative;
  box-sizing: border-box;
  height: auto;
  overflow: visible;
  page-break-before: always;
  page-break-after: always;

  &.a4 {
    width: $a4-width;
    min-height: $a4-height;
    padding: $a4-vertical-padding $a4-horizontal-padding;

    .cover-image-section {
      margin-bottom: 30px;
    }

    .cover-image {
      max-height: 180px;
    }

    .society-info-section {
      gap: 15px;
      margin-bottom: 20px;
    }

    .society-logo {
      width: 100px;
    }

    .society-name-en {
      font-size: 11px;
    }

    .society-name-ko {
      font-size: 14px;
    }

    .divider {
      margin: 20px 0;
    }

    .paper-list-section {
      gap: 16px;
    }

    .paper-item {
      padding-bottom: 12px;
    }

    .paper-title {
      font-size: 11px;
    }

    .paper-title-eng {
      font-size: 11px;
    }

    .paper-author {
      font-size: 10px;
    }

    .issn-section {
      bottom: $a4-vertical-padding;
      left: $a4-horizontal-padding;
      right: $a4-horizontal-padding;
    }

    .issn-text {
      font-size: 12px;
    }
  }

  &.a5 {
    width: $a5-width;
    min-height: $a5-height;
    padding: $a5-vertical-padding $a5-horizontal-padding;

    .cover-image-section {
      margin-bottom: 20px;
    }

    .cover-image {
      max-height: 120px;
    }

    .society-info-section {
      gap: 10px;
      margin-bottom: 15px;
    }

    .society-logo {
      width: 70px;
    }

    .society-name-en {
      font-size: 8px;
    }

    .society-name-ko {
      font-size: 10px;
    }

    .divider {
      margin: 12px 0;
    }

    .paper-list-section {
      gap: 10px;
    }

    .paper-item {
      padding-bottom: 8px;
    }

    .paper-title {
      font-size: 9px;
    }

    .paper-title-eng {
      font-size: 8px;
    }

    .paper-author {
      font-size: 8px;
    }

    .issn-section {
      bottom: $a5-vertical-padding;
      left: $a5-horizontal-padding;
      right: $a5-horizontal-padding;
    }

    .issn-text {
      font-size: 10px;
    }
  }
}

/** ****************************************************************************************
 * [ 커버 이미지 섹션 ]
 *******************************************************************************************/
.cover-image-section {
  display: flex;
  justify-content: center;
}

.cover-image {
  width: 100%;
  height: auto;
  object-fit: cover;
}

/** ****************************************************************************************
 * [ 학회 정보 섹션 ]
 *******************************************************************************************/
.society-info-section {
  display: flex;
  flex-direction: row;
  align-items: center;
}

.society-logo {
  height: auto;
}

.society-text {
  display: flex;
  flex-direction: column;
}

.society-name-en {
  font-family: $sans-font-family;
  font-weight: 600;
  color: $primary-font-color;
  text-align: left;
  margin: 0;
}

.society-name-ko {
  font-family: $sans-font-family;
  font-weight: 700;
  color: $primary-font-color;
  text-align: left;
  margin: 0;
}

/** ****************************************************************************************
 * [ 구분선 ]
 *******************************************************************************************/
.divider {
  border: none;
  border-top: 1px solid $primary-font-color;
}

/** ****************************************************************************************
 * [ 논문 목록 섹션 ]
 *******************************************************************************************/
.paper-list-section {
  display: flex;
  flex-direction: column;
}

.paper-item {
  border-bottom: 1px dotted $secondary-font-color;
}

.paper-title {
  font-family: $sans-font-family;
  font-weight: 600;
  color: $primary-font-color;
  margin: 0 0 4px 0;
  line-height: 1.5;
}

.paper-title-eng {
  font-family: $sans-font-family;
  font-weight: 400;
  color: $primary-font-color;
  margin: 0 0 6px 0;
  line-height: 1.4;
}

.paper-author {
  font-family: $sans-font-family;
  font-weight: 400;
  color: $secondary-font-color;
  margin: 0;
}

/** ****************************************************************************************
 * [ ISSN 섹션 ]
 *******************************************************************************************/
.issn-section {
  position: absolute;
  text-align: center;
}

.issn-text {
  font-family: $sans-font-family;
  font-weight: 600;
  color: $primary-font-color;
  margin: 0;
}

@media print {
  .page.a5 {
    padding: 30px 36px;
  }

  .page.a5 .cover-image-section {
    margin-bottom: 16px;
  }

  .page.a5 .cover-image {
    max-height: 110px;
  }

  .page.a5 .society-info-section {
    margin-bottom: 12px;
  }

  .page.a5 .divider {
    margin: 10px 0;
  }

  .page.a5 .paper-list-section {
    gap: 8px;
  }

  .page.a5 .paper-item {
    padding-bottom: 6px;
  }

  .page.a5 .paper-title {
    font-size: 8.5px;
    line-height: 1.35;
  }

  .page.a5 .paper-title-eng {
    font-size: 7.5px;
    margin: 0 0 4px 0;
    line-height: 1.3;
  }

  .page.a5 .paper-author {
    font-size: 7.5px;
  }

  .page.a5 .issn-text {
    font-size: 9px;
  }
}
</style>
